import { setItem, getItem, removeItem } from './storage';
import { BASE_URL } from './api';

import { autoSubscribeIfNeeded } from './pushNotificationService';
// ============================================
// AUTHENTICATION
// ============================================

// Login
// Login
export const login = async (email, password) => {
  try {
    // console.log('🔐 [auth.js] Login attempt for:', email);
    
    const response = await fetch(`${BASE_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    // console.log('📤 [auth.js] Response status:', response.status);
    
    const data = await response.json();
    // console.log('📥 [auth.js] Response data:', data);

    if (!response.ok) {
      // console.error('❌ [auth.js] Server error response:', data);
      
      if (data.needs_verification) {
        throw {
          needsVerification: true,
          userId: data.user_id,
          email: data.email,
          message: data.message || 'Please verify your email first'
        };
      }
      throw new Error(data.error || 'Login failed');
    }

    // console.log('✅ [auth.js] Login successful!');
    
    // Store auth data
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    
    // 🔥 AUTO-SUBSCRIBE TO PUSH NOTIFICATIONS
    // Wait a bit for the app to stabilize, then request push permission
    setTimeout(() => {
      autoSubscribeIfNeeded().then(subscribed => {
        if (subscribed) {
          console.log('🔔 User auto-subscribed to push notifications');
        } else {
          console.log('ℹ️ Push auto-subscribe skipped or failed');
        }
      }).catch(err => {
        console.log('⚠️ Push auto-subscribe error:', err.message);
      });
    }, 2000); // 2 second delay
    
    const result = {
      success: true,
      user: data.user,
      token: data.token,
      hasNotifications: data.has_notifications || false
    };
    
    // console.log('🎯 [auth.js] Returning:', result);
    return result;
    
  } catch (error) {
    // console.error('🔥 [auth.js] Login error:', error);
    throw error;
  }
};

// Register
// Register
export const register = async (userData) => {
  try {
    const response = await fetch(`${BASE_URL}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: userData.email,
        password: userData.password,
        firstName: userData.firstName,
        lastName: userData.lastName,
        phoneNumber: userData.phoneNumber
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Registration failed');
    }

    // 🔥 If registration was successful and user is automatically logged in,
    // we could store token and auto-subscribe
    if (data.token) {
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      
      // Auto-subscribe after registration
      setTimeout(() => {
        autoSubscribeIfNeeded().catch(err => 
          console.log('⚠️ Push auto-subscribe error:', err.message)
        );
      }, 2000);
    }

    return {
      success: data.success,
      message: data.message,
      userId: data.user_id,
      needsVerification: data.needs_verification || true,
      hasNotifications: data.has_notifications || false
    };
    
  } catch (error) {
    // console.error('Registration error:', error);
    throw error;
  }
};

// ============================================
// EMAIL VERIFICATION
// ============================================

// Verify email with OTP
export const verifyEmail = async (userId, otpCode) => {
  try {
    const response = await fetch(`${BASE_URL}/auth/verify-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        user_id: userId, 
        code: otpCode 
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Email verification failed');
    }

    return {
      success: data.success,
      message: data.message,
      email: data.email,
      notificationsSent: data.notifications_sent || false,
      emailsSent: data.emails_sent || false
    };
    
  } catch (error) {
    // console.error('Email verification error:', error);
    throw error;
  }
};

// Resend verification email
export const resendVerificationEmail = async (userId, email) => {
  try {
    const response = await fetch(`${BASE_URL}/auth/resend-verification`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        user_id: userId, 
        email: email 
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to resend verification email');
    }

    return {
      success: data.success,
      message: data.message,
      userId: data.user_id,
      email: data.email,
      codeValidUntil: data.code_valid_until,
      notificationSent: data.notification_sent || false
    };
    
  } catch (error) {
    // console.error('Resend verification error:', error);
    throw error;
  }
};

// Get verification status
export const getVerificationStatus = async (userId) => {
  try {
    const response = await fetch(`${BASE_URL}/auth/verification-status`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to get verification status');
    }

    return {
      success: data.success,
      status: data.data
    };
    
  } catch (error) {
    // console.error('Verification status error:', error);
    throw error;
  }
};

// ============================================
// PASSWORD RESET
// ============================================

// Forgot password - Request OTP
export const forgotPassword = async (email) => {
  try {
    const response = await fetch(`${BASE_URL}/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });

    const data = await response.json();

    if (!response.ok) {
      // Check for rate limiting
      if (response.status === 429) {
        throw {
          rateLimited: true,
          retryAfter: data.retry_after,
          message: data.error
        };
      }
      throw new Error(data.error || 'Failed to send password reset OTP');
    }

    return {
      success: data.success,
      message: data.message,
      emailSent: data.email_sent || false,
      otpExpiresIn: data.otp_expires_in || '15 minutes',
      notificationSent: data.notification_sent || false,
      userId: data.user_id // This is important for the next step
    };
    
  } catch (error) {
    // console.error('Forgot password error:', error);
    throw error;
  }
};

// Verify password reset OTP
export const verifyPasswordResetOTP = async (email, otpCode) => {
  try {
    const response = await fetch(`${BASE_URL}/auth/verify-password-reset-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        email: email,
        otp_code: otpCode 
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Invalid OTP code');
    }

    return {
      success: data.success,
      valid: data.valid,
      message: data.message,
      userId: data.user_id,
      email: data.email,
      firstName: data.first_name,
      lastName: data.last_name,
      expiresAt: data.expires_at,
      token: data.token // Optional security token
    };
    
  } catch (error) {
    // console.error('Verify password reset OTP error:', error);
    throw error;
  }
};

// Reset password with OTP verification
export const resetPassword = async (email, otpCode, newPassword, confirmPassword) => {
  try {
    const response = await fetch(`${BASE_URL}/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        email: email,
        otp_code: otpCode,
        password: newPassword,
        confirm_password: confirmPassword
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to reset password');
    }

    return {
      success: data.success,
      message: data.message,
      emailSent: data.email_sent || false,
      notificationSent: data.notification_sent || false
    };
    
  } catch (error) {
    // console.error('Reset password error:', error);
    throw error;
  }
};

// Alternative reset password with user ID (after OTP verification)
export const resetPasswordWithUserId = async (userId, email, newPassword, confirmPassword) => {
  try {
    const response = await fetch(`${BASE_URL}/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        user_id: userId,
        email: email,
        password: newPassword,
        confirm_password: confirmPassword
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to reset password');
    }

    return {
      success: data.success,
      message: data.message,
      emailSent: data.email_sent || false,
      notificationSent: data.notification_sent || false
    };
    
  } catch (error) {
    // console.error('Reset password with user ID error:', error);
    throw error;
  }
};

// Resend password reset OTP
export const resendPasswordResetOTP = async (email, userId = null) => {
  try {
    const payload = { email };
    if (userId) {
      payload.user_id = userId;
    }

    const response = await fetch(`${BASE_URL}/auth/resend-password-reset-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to resend OTP');
    }

    return {
      success: data.success,
      message: data.message,
      emailSent: data.email_sent || false,
      otpExpiresIn: data.otp_expires_in || '15 minutes',
      notificationSent: data.notification_sent || false,
      userId: data.user_id
    };
    
  } catch (error) {
    // console.error('Resend password reset OTP error:', error);
    throw error;
  }
};

// ============================================
// SESSION MANAGEMENT
// ============================================

// Logout
export const logout = async () => {
  try {
    await removeItem('token');
    await removeItem('user');
    return { success: true, message: 'Logged out successfully' };
  } catch (error) {
    // console.error('Logout error:', error);
    throw error;
  }
};

// Check if user is logged in
export const isLoggedIn = async () => {
    try {
      // FIX: Use localStorage directly
      const token = localStorage.getItem('token');
      const userString = localStorage.getItem('user');
      
      if (token && userString) {
        const user = JSON.parse(userString);
        return {
          isLoggedIn: true,
          user: user,
          token: token
        };
      }
      
      return { isLoggedIn: false };
    } catch (error) {
      // console.error('Check login error:', error);
      return { isLoggedIn: false };
    }
  };

// Get current user
export const getCurrentUser = async () => {
  try {
    const userString = await getItem('user');
    if (userString) {
      return JSON.parse(userString);
    }
    return null;
  } catch (error) {
    // console.error('Get current user error:', error);
    return null;
  }
};

// Get auth token
export const getAuthToken = async () => {
  try {
    return await getItem('token');
  } catch (error) {
    // console.error('Get auth token error:', error);
    return null;
  }
};

// ============================================
// PROFILE MANAGEMENT
// ============================================

// Upload profile picture
export const uploadProfilePicture = async (userId, imageUri) => {
  try {
    // Create FormData for file upload
    const formData = new FormData();
    formData.append('user_id', userId);
    formData.append('image', {
      uri: imageUri,
      type: 'image/jpeg',
      name: 'profile_picture.jpg',
    });

    const response = await fetch(`${BASE_URL}/auth/upload-profile-picture`, {
      method: 'POST',
      body: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to upload profile picture');
    }

    return {
      success: true,
      url: data.data?.url,
      message: 'Profile picture uploaded successfully'
    };
    
  } catch (error) {
    // console.error('Upload profile picture error:', error);
    throw error;
  }
};

// ============================================
// HELPER FUNCTIONS
// ============================================

// Make authenticated API calls
export const authFetch = async (endpoint, options = {}) => {
  try {
    const token = await getAuthToken();
    
    const defaultHeaders = {
      'Content-Type': 'application/json',
    };
    
    if (token) {
      defaultHeaders['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${BASE_URL}/${endpoint}`, {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    });

    // Handle unauthorized (token expired)
    if (response.status === 401) {
      await logout();
      throw new Error('Session expired. Please login again.');
    }

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Request failed');
    }

    return data;
    
  } catch (error) {
    // console.error('Auth fetch error:', error);
    throw error;
  }
};

// Validate email format
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Validate password strength
export const validatePassword = (password) => {
  const errors = [];
  
  if (password.length < 8) {
    errors.push('Password must be at least 8 characters');
  }
  
  if (!/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }
  
  if (!/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  }
  
  if (!/[0-9]/.test(password)) {
    errors.push('Password must contain at least one number');
  }
  
  return {
    isValid: errors.length === 0,
    errors: errors
  };
};

// ============================================
// EXPORT ALL FUNCTIONS
// ============================================

export default {
  // Authentication
  login,
  register,
  logout,
  isLoggedIn,
  getCurrentUser,
  getAuthToken,
  
  // Email Verification
  verifyEmail,
  resendVerificationEmail,
  getVerificationStatus,
  
  // Password Reset
  forgotPassword,
  verifyPasswordResetOTP,
  resetPassword,
  resetPasswordWithUserId,
  resendPasswordResetOTP,
  
  // Profile
  uploadProfilePicture,
  
  // Helpers
  authFetch,
  isValidEmail,
  validatePassword,
  
  // Constants
  BASE_URL
};